/* eslint-disable no-unused-vars, react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { Button } from 'src/components/ui/button';
import {
  FileText,
  GraduationCap,
  Briefcase,
  MapPin,
  Calendar,
  Zap,
  Check,
  Globe,
  UserPlus,
  History,
  Eye,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useAuth } from 'src/hooks/useAuth';
import {
  useOpportunityDetails,
  useRequirementLocations,
} from 'src/services/api/queries/opportunityQueries';
import {
  useFieldOfStudyMap,
  useLanguageMap,
} from 'src/services/api/queries/referenceDataQueries';
import {
  useAssignToTa,
  useAssignPartnerToAssignment,
  usePublishAssignment,
  useClaimOpportunity,
} from 'src/services/api/mutations/opportunityMutations';
import { cn } from 'src/lib/utils';
import HistoryModal from 'src/components/Modals/HistoryModal';
import { Alert } from 'src/components/common/Alert';
import MarkdownBody from 'src/components/common/MarkdownBody';
import OpportunityActions from './OpportunityActions';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getQualificationDisplayName = (t) => {
  const MAP = {
    TENTH: 'Secondary',
    TWELFTH: 'Higher Secondary',
    DEGREE: "Bachelor's Degree",
    MASTERS: "Master's Degree",
  };
  return MAP[t] || t || '';
};

const transformApiResponse = (apiData) => {
  const {
    requirement_id: id,
    registration_id: regId,
    title,
    career_level: careerLevel,
    detailed_job_description: detailedJobDescription,
    additional_requirements: additionalRequirements,
    minimum_salary: minimumSalary,
    maximum_salary: maximumSalary,
    employment_type: employmentType,
    min_work_experience: minWorkExperience,
    max_work_experience: maxWorkExperience,
    closing_date: closingDate,
    created_at: createdAt,
    archived_at: archivedAt,
    status,
    calculated_requirement: calculatedRequirement,
    offers_made: offersMade,
    locations,
    qualification_requirements: qualificationRequirements,
    skills,
    certifications,
    languages,
    selection_process: selectionProcess,
    routing_status: routingStatus,
    priority,
    is_published: isPublished,
    // 0 (or absent) means no CSE has claimed this requirement yet — same
    // field Opportunities.jsx's own card list gates its New-tab Claim
    // button on (row?.current_cse_id === 0). Not read here before now.
    current_cse_id: currentCseId,
  } = apiData;

  const safeLocations = Array.isArray(locations) ? locations : [];
  const headcount = safeLocations.reduce(
    (sum, loc) => sum + (loc.headcount || 0),
    0
  );

  const monthsToYrsLabel = (months) => {
    if (months == null) return null;
    const y = months / 12;
    return y % 1 === 0 ? `${y}` : y.toFixed(1);
  };

  let workExperience = null;
  if (minWorkExperience != null && maxWorkExperience != null) {
    workExperience = `${monthsToYrsLabel(minWorkExperience)} – ${monthsToYrsLabel(maxWorkExperience)} yrs`;
  } else if (minWorkExperience != null) {
    workExperience = `${monthsToYrsLabel(minWorkExperience)}+ yrs`;
  } else if (maxWorkExperience != null) {
    workExperience = `Up to ${monthsToYrsLabel(maxWorkExperience)} yrs`;
  }

  return {
    id,
    regId,
    title,
    headcount,
    careerLevel,
    description: detailedJobDescription,
    additionalRequirements,
    minimumSalary,
    maximumSalary,
    employmentType,
    workExperience,
    closingDate: closingDate ? new Date(closingDate) : null,
    postedOn: createdAt ? new Date(createdAt) : null,
    archivedOn: archivedAt ? new Date(archivedAt) : null,
    status,
    calculatedRequirement,
    offersMade,
    profilesSubmitted: null,
    locations: safeLocations,
    qualifications: (qualificationRequirements || []).flatMap((qual) =>
      (qual.options || []).map((opt) => ({
        qualification_type: opt.qualification_type,
        field_of_study_id: opt.field_of_study_id ?? null,
        match_any_field: !!opt.match_any_field,
        is_mandatory: qual.is_mandatory ?? false,
      }))
    ),
    selectionProcess,
    skills: (skills || []).map((s) => ({
      skill_id: s.name || s.skill_name,
      is_mandatory: s.is_mandatory,
    })),
    certifications: (certifications || []).map((c) => ({
      certification_id: c.name || c.certification_name,
      is_mandatory: c.is_mandatory,
    })),
    languages: (languages || []).map((l) => ({
      language_id: l.language_id,
      min_proficiency: l.min_proficiency,
      is_mandatory: l.is_mandatory ?? false,
    })),
    routingStatus,
    priority,
    isPublished,
    currentCseId,
  };
};

// ── UI primitives ─────────────────────────────────────────────────────────────

const MetaDot = () => (
  <span
    style={{
      width: 3,
      height: 3,
      borderRadius: '50%',
      background: '#919BAA',
      opacity: 0.55,
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

const ReqStatusPill = ({ status }) => {
  const MAP = {
    ACTIVE: {
      cls: 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800',
      label: 'Active',
    },
    PUBLISHED: {
      cls: 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800',
      label: 'Published',
    },
    DRAFT: {
      cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
      label: 'Draft',
    },
    REVIEW: {
      cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
      label: 'Under Review',
    },
    CLOSED: {
      cls: 'bg-muted text-muted-foreground border border-border',
      label: 'Closed',
    },
  };
  const s = MAP[(status || '').toUpperCase()] || MAP.CLOSED;
  return (
    <span
      className={s.cls}
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: 5,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
};

const RoCard = ({ icon, title, children }) => (
  <section
    className="bg-card border border-border"
    style={{
      borderRadius: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,.04)',
      overflow: 'hidden',
    }}
  >
    <div
      className="text-foreground-strong border-b border-border"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '13px 18px',
        fontSize: 13.5,
        fontWeight: 650,
      }}
    >
      <span
        className="bg-[#E9F1FA] dark:bg-[#1B3A5C] text-[#2068B2] dark:text-[#5A9FD8]"
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      {title}
    </div>
    <div style={{ padding: '16px 18px' }}>{children}</div>
  </section>
);

const GlanceCell = ({ label, value }) => (
  <div className="bg-muted" style={{ padding: '11px 13px', borderRadius: 9 }}>
    <div
      className="text-muted-foreground"
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      className={value ? 'text-foreground-strong' : 'text-muted-foreground'}
      style={{
        fontSize: 13,
        fontWeight: value ? 600 : 400,
        fontStyle: value ? 'normal' : 'italic',
      }}
    >
      {value || 'Not specified'}
    </div>
  </div>
);

const GlanceRow = ({ label, value, last }) => (
  <div
    className={cn(
      'flex justify-between items-baseline gap-[14px] py-[9px]',
      !last && 'border-b border-border'
    )}
  >
    <span className="text-muted-foreground" style={{ fontSize: 12.5 }}>
      {label}
    </span>
    <span
      className={value ? 'text-foreground-strong' : 'text-muted-foreground'}
      style={{
        fontSize: 13,
        fontWeight: value ? 600 : 400,
        fontStyle: value ? 'normal' : 'italic',
        textAlign: 'right',
      }}
    >
      {value || 'Not specified'}
    </span>
  </div>
);

const RoSub = ({ children, topGap = 0 }) => (
  <div
    className="text-muted-foreground"
    style={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      marginBottom: 9,
      marginTop: topGap,
    }}
  >
    {children}
  </div>
);

const RoEmpty = ({ children }) => (
  <div
    className="text-muted-foreground"
    style={{ fontSize: 12.5, fontStyle: 'italic' }}
  >
    {children}
  </div>
);

const SkillChip = ({ label, mandatory }) => {
  // Colours sourced from design-system tokens (theme-aware via CSS vars).
  // Required → brand/primary accent; Preferred → neutral/grey.
  const accent = mandatory
    ? 'hsl(var(--primary))'
    : 'hsl(var(--muted-foreground))';
  const chipStyle = mandatory
    ? {
        background: 'hsl(var(--brand-bg-subtle))',
        border: `1px solid color-mix(in srgb, ${accent} 22%, transparent)`,
      }
    : {
        background: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
      };
  const badgeBg = `color-mix(in srgb, ${accent} 14%, transparent)`;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px',
        borderRadius: 6,
        fontSize: 12.5,
        fontWeight: 500,
        color: accent,
        ...chipStyle,
      }}
    >
      {label}
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          padding: '1px 5px',
          borderRadius: 3,
          letterSpacing: '.03em',
          background: badgeBg,
          color: accent,
        }}
      >
        {mandatory ? 'REQ' : 'OPT'}
      </span>
    </span>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const OpportunityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { getUserInfo } = useAuth();
  const [testCollectionId, setTestCollectionId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [claimConfirmOpen, setClaimConfirmOpen] = useState(false);

  const user = getUserInfo();
  const isCse = user?.roles?.includes('ahedge_cse');

  const requirementLocationId =
    searchParams.get('requirementLocationId') || null;
  const TaAssignmentId = searchParams.get('assignmentId');
  const candidateRequestedForTa = searchParams.get('candidateRequested');

  const {
    data: opportunityResponse,
    isLoading,
    error,
    refetch,
  } = useOpportunityDetails(id, {
    requirementLocationId,
    assignmentId: TaAssignmentId,
  });
  const {
    data: requirementLocationsResponse,
    isLoading: isRequirementLocationsLoading,
  } = useRequirementLocations(id, { assignmentId: TaAssignmentId });

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (opportunityResponse?.data?.test_collection_id) {
      setTestCollectionId(opportunityResponse.data.test_collection_id);
    }
  }, [opportunityResponse]);

  const assignToTaMutation = useAssignToTa();
  const assignPartnerToAssignmentMutation = useAssignPartnerToAssignment();
  const publishAssignmentMutation = usePublishAssignment();
  const claimOpportunityMutation = useClaimOpportunity();

  const fieldOfStudyMap = useFieldOfStudyMap();
  const languageMap = useLanguageMap();

  const opportunityData = opportunityResponse?.data
    ? transformApiResponse(opportunityResponse.data)
    : null;
  // A CSE has claimed the opportunity once it goes ACTIVE (there is only one CSE
  // in the system for now, so status is a reliable proxy for "assigned").
  const isAssigned = opportunityData?.status === 'ACTIVE';
  // Distinct from isAssigned above (that's about TA-routing eligibility,
  // gated on status) — this is specifically "no CSE owns this requirement
  // yet," the same current_cse_id === 0 check Opportunities.jsx's own New
  // tab card list already uses to show its Claim button.
  //
  // GET .../requirements/:id/details (this page's own fetch) doesn't
  // actually carry current_cse_id at all — confirmed live it's always
  // undefined here, only the list endpoint returns it. Falls back to the
  // same field Opportunities.jsx's card click now carries over as router
  // state (see its own handleCardClick comment) so Claim still shows for
  // the real, expected path (click a New-tab card → land here); arriving
  // by a direct URL/refresh loses that state and simply won't show Claim,
  // same as today. The `?? location.state?.currentCseId` order means a
  // future backend field on this endpoint would take precedence the
  // moment it exists, with no further change needed here.
  const currentCseId =
    opportunityData?.currentCseId ?? location.state?.currentCseId;
  const isClaimable = isCse && currentCseId === 0;
  // No CSE owns this requirement yet (0, or unknown because neither the
  // details response nor router state carried it — see currentCseId's own
  // comment above). View Matches / Profiles Submitted act on candidates
  // against this requirement, which can't exist until a CSE has claimed
  // it, so hide rather than show them disabled.
  const isUnclaimed = isCse && (currentCseId == null || currentCseId === 0);

  let routingStatus = '';
  let requirementLocations = [];
  if (isCse) {
    routingStatus =
      requirementLocationsResponse?.data?.requirement_info?.ta_routing_status;
    requirementLocations =
      requirementLocationsResponse?.data?.requirement_locations || [];
  } else {
    routingStatus = requirementLocationsResponse?.data?.assignment_info
      ?.assignment_id
      ? 'ASSIGNED'
      : '';
    requirementLocations =
      requirementLocationsResponse?.data?.assignment_locations || [];
  }

  // The Publish/Engage Institution swap in OpportunityActions is TA-only,
  // so this only ever matters there — assignment-locations' own
  // assignment_info.is_published is the live source of truth for it
  // (confirmed live: true here while opportunityData.isPublished, sourced
  // from the details endpoint, still read false for the same assignment).
  // Falls back to the details field so a CSE view (which never reads this
  // prop anyway) or a response missing assignment_info doesn't break.
  const isPublished =
    requirementLocationsResponse?.data?.assignment_info?.is_published ??
    opportunityData?.isPublished;

  const isDraftOrReview = ['DRAFT', 'REVIEW'].includes(opportunityData?.status);

  // CSE → TA (direct): opens the requirement's locations up for any active
  // TA to see and submit against — no TA/TA Lead is targeted.
  const handleForwardToTa = async (data) => {
    try {
      const response = await assignToTaMutation.mutateAsync({
        opportunityId: id,
        data,
      });
      if (response?.success) {
        toast({
          message: 'Requirement opened for TA sourcing successfully',
          variant: 'success',
        });
        return true;
      }
      throw new Error(response?.message || 'Failed to forward to TA');
    } catch (err) {
      console.error('Error forwarding to TA:', err);
      toast({
        message: err?.message || 'Failed to forward to TA',
        variant: 'destructive',
      });
      return false;
    } finally {
      refetch();
    }
  };

  // Same call/toast shape Opportunities.jsx's own OppCard "Claim" button
  // already uses — kept consistent rather than inventing a second pattern
  // for the same action reached from a second entry point.
  const handleClaim = async () => {
    try {
      await claimOpportunityMutation.mutateAsync(id);
      toast({
        message: 'Opportunity claimed and work order generated.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to claim opportunity',
        variant: 'destructive',
      });
    }
  };

  const handleClaimClick = () => setClaimConfirmOpen(true);

  const handleAssignPartner = async (data) => {
    try {
      const partnerAssignments = data?.assignments || [];
      if (!TaAssignmentId && !data?.assignmentId)
        throw new Error('Missing assignment id for partner assignment.');
      const assignmentIdForPartner = TaAssignmentId || data.assignmentId;
      if (!partnerAssignments.length)
        throw new Error('No partner assignments provided.');
      await Promise.all(
        partnerAssignments.map((assignment) =>
          assignPartnerToAssignmentMutation.mutateAsync({
            assignmentId: assignmentIdForPartner,
            data: {
              partner_org_id: assignment.partner_org_id,
              deadline: assignment.deadline,
              requirement_locations: assignment.requirement_locations,
            },
          })
        )
      );
      toast({
        message: `Assigned ${partnerAssignments.length} partner${partnerAssignments.length > 1 ? 's' : ''}.`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        message: err.message || 'Failed to assign partner.',
        variant: 'destructive',
      });
    }
  };

  const handlePublishAssignment = async (data) => {
    try {
      if (!TaAssignmentId)
        throw new Error('Missing assignment id for publish.');
      const response = await publishAssignmentMutation.mutateAsync({
        assignmentId: TaAssignmentId,
        data,
      });
      if (response?.success) {
        toast({
          message: 'Assignment published successfully.',
          variant: 'success',
        });
        return true;
      }
      throw new Error(response?.message || 'Failed to publish assignment.');
    } catch (err) {
      toast({
        message: err?.message || 'Failed to publish assignment.',
        variant: 'destructive',
      });
      return false;
    } finally {
      refetch();
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Opportunities', href: '/opportunities' },
            { label: 'Loading…' },
          ]}
        />
        <div
          className="bg-muted"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 3.4rem)',
          }}
        >
          <p className="text-muted-foreground" style={{ fontSize: 13 }}>
            Loading requirement info…
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Opportunities', href: '/opportunities' },
            { label: 'Error' },
          ]}
        />
        <div
          className="bg-muted dark:bg-background"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 3.4rem)',
          }}
        >
          <div
            className="bg-card border border-destructive/20"
            style={{
              textAlign: 'center',
              padding: '24px 32px',
              borderRadius: 12,
              maxWidth: 360,
            }}
          >
            <div
              className="text-destructive"
              style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
            >
              Failed to load requirement
            </div>
            <div
              className="text-muted-foreground"
              style={{ fontSize: 13, marginBottom: 16 }}
            >
              {error.message}
            </div>
            <Button size="sm" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (!opportunityData) return <div>No requirement data found.</div>;

  // derived
  const parseSalary = (v) =>
    v ? parseInt(String(v).replace(/,/g, ''), 10) : null;
  const minLPA = parseSalary(opportunityData.minimumSalary);
  const maxLPA = parseSalary(opportunityData.maximumSalary);
  const salaryRange =
    minLPA && maxLPA
      ? `₹${(minLPA / 100000).toFixed(1)} – ₹${(maxLPA / 100000).toFixed(1)} LPA`
      : null;
  const totalOpenings = opportunityData.headcount || 0;
  // TA/TA Lead see the count they were asked to source for this assignment
  // (candidates_requested, summed across assignment_locations) instead of
  // the requirement's total openings.
  const totalRequested = requirementLocations.reduce(
    (sum, loc) => sum + (loc.candidates_requested || 0),
    0
  );
  const reqSkills = (opportunityData.skills || []).filter(
    (s) => s.is_mandatory
  );
  const optSkills = (opportunityData.skills || []).filter(
    (s) => !s.is_mandatory
  );
  const allEdu = opportunityData.qualifications || [];
  const allCerts = opportunityData.certifications || [];
  const allLangs = opportunityData.languages || [];

  const PROFICIENCY_LABEL = {
    NATIVE: 'Native',
    FLUENT: 'Fluent',
    PROFESSIONAL: 'Professional',
    INTERMEDIATE: 'Intermediate',
    BASIC: 'Basic',
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Opportunities', href: '/opportunities' },
          { label: opportunityData.title },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {/* variant="outline" + size="headerSm" — matches Create
                Requirement's own header button (Opportunities.jsx) instead
                of outlineV2, which reads bg-background rather than bg-card.
                PageHeader's own surface (.headerBg) IS bg-card, so in light
                mode (where --background and --card happen to be the same
                white) outlineV2 looked fine by coincidence — in dark mode
                they diverge, and outlineV2's fill sat visibly off from the
                header behind it instead of blending the way an outline
                button on this surface should. `outline` reads bg-card, so
                it matches unconditionally in both themes.
                Label/icon also split by role: a CSE landing on
                /candidates/submit only ever browses matches read-only
                (that page's own selection/submit UI is hidden for isCse,
                same reasoning as RequirementPipeline.jsx's own header button) —
                "View Matches" + Eye reflects that, rather than implying a
                submit action CSE can't actually take there. */}
            {(!isCse || !isUnclaimed) && (
              <Button
                variant="outline"
                size="headerSm"
                className="gap-1.5"
                disabled={isDraftOrReview}
                onClick={() =>
                  navigate(
                    `/candidates/submit?opportunityId=${id}${TaAssignmentId ? `&assignmentId=${TaAssignmentId}` : ''}${requirementLocationId ? `&requirementLocationId=${requirementLocationId}` : ''}`
                  )
                }
              >
                {isCse ? (
                  <Eye className="h-3.5 w-3.5" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                {isCse ? 'View Matches' : 'Submit Candidates'}
              </Button>
            )}
            {isCse && !isUnclaimed && (
              <Button
                variant="primary"
                size="default"
                disabled={isDraftOrReview}
                onClick={() => {
                  const p = new URLSearchParams({
                    status: 'submitted',
                    opportunityId: id,
                  });
                  if (requirementLocationId)
                    p.set('requirementLocationId', requirementLocationId);
                  navigate(`/candidates/list?${p.toString()}`);
                }}
              >
                Profiles Submitted
              </Button>
            )}
            {!isCse && routingStatus === 'ASSIGNED' && (
              <Button
                variant="primary"
                size="default"
                onClick={() => {
                  const p = new URLSearchParams({
                    status: 'new',
                    opportunityId: id,
                  });
                  if (requirementLocationId)
                    p.set('requirementLocationId', requirementLocationId);
                  if (TaAssignmentId) p.set('assignmentId', TaAssignmentId);
                  navigate(`/assignment-pipeline?${p.toString()}`);
                }}
              >
                Profile Submitted
              </Button>
            )}
            {isCse && (
              <Button
                variant="outline"
                size="headerSm"
                onClick={() => setHistoryOpen(true)}
                aria-label="View activity history"
                title="Activity history"
                className="px-2"
              >
                <History className="h-4 w-4" />
              </Button>
            )}
          </div>
        }
      />

      <div
        className="bg-muted"
        style={{
          minHeight: 'calc(100vh - 3.4rem)',
          padding: '28px 36px',
        }}
      >
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {/* ── Detail head ── */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 20,
              flexWrap: 'wrap',
              marginBottom: 22,
              width: '100%',
            }}
          >
            <div className="w-full">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <h1
                  className="text-foreground-strong"
                  style={{
                    fontSize: 24,
                    fontWeight: 750,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    margin: 0,
                  }}
                >
                  {opportunityData.title}
                </h1>
                <ReqStatusPill status={opportunityData.status} />
              </div>
              <div className="flex justify-between w-full flex-wrap mt-4">
                <div
                  className="text-muted-foreground flex items-center gap-2 flex-wrap"
                  style={{
                    marginTop: 10,
                    fontSize: 12.5,
                  }}
                >
                  {opportunityData.regId && (
                    <>
                      <b
                        className="text-foreground-strong"
                        style={{ fontWeight: 600 }}
                      >
                        {opportunityData.regId}
                      </b>
                      <MetaDot />
                    </>
                  )}
                  {opportunityData.postedOn && (
                    <>
                      <span>Posted {fmtDate(opportunityData.postedOn)}</span>
                      <MetaDot />
                    </>
                  )}
                  {opportunityData.closingDate && (
                    <>
                      <span>Closes {fmtDate(opportunityData.closingDate)}</span>
                      <MetaDot />
                    </>
                  )}
                  {opportunityData.archivedOn && (
                    <>
                      <span>
                        Archived {fmtDate(opportunityData.archivedOn)}
                      </span>
                      <MetaDot />
                    </>
                  )}
                  {isCse ? (
                    <span>
                      <b className="text-foreground-strong">{totalOpenings}</b>{' '}
                      opening
                      {totalOpenings !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span>
                      <b className="text-foreground-strong">{totalRequested}</b>{' '}
                      requested
                    </span>
                  )}
                  {isCse && routingStatus === 'ASSIGNED' && (
                    <>
                      <MetaDot />
                      <button
                        type="button"
                        onClick={() => {
                          const p = new URLSearchParams({
                            status: 'screened',
                            opportunityId: id,
                          });
                          if (requirementLocationId)
                            p.set(
                              'requirementLocationId',
                              requirementLocationId
                            );
                          if (TaAssignmentId)
                            p.set('assignmentId', TaAssignmentId);
                          navigate(`/candidates/list?${p.toString()}`);
                        }}
                        className="text-primary"
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        Sourced by TA
                      </button>
                    </>
                  )}
                </div>
                <OpportunityActions
                  opportunityId={id}
                  isAssigned={isAssigned}
                  isClaimable={isClaimable}
                  onClaim={handleClaimClick}
                  isClaiming={claimOpportunityMutation.isPending}
                  isPublished={isPublished}
                  publishInitialValues={{
                    title: opportunityData.title || '',
                    jobDescription: opportunityData.description || '',
                    additionalDetails:
                      opportunityData.additionalRequirements || '',
                    deadline:
                      requirementLocationsResponse?.data?.assignment_info
                        ?.deadline || null,
                  }}
                  onAssignToTa={handleForwardToTa}
                  onAssignPartner={handleAssignPartner}
                  onPublishAssignment={handlePublishAssignment}
                  routingStatus={routingStatus}
                  deadline={opportunityData.closingDate}
                  testCollectionId={testCollectionId}
                  assId={TaAssignmentId}
                />
              </div>
            </div>
          </div>

          {/* ── Detail grid ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* At a glance */}
            <RoCard icon={<Briefcase size={16} />} title="At a glance">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: 10,
                }}
              >
                <GlanceCell
                  label="Experience"
                  value={opportunityData.workExperience || null}
                />
                <GlanceCell label="Salary range" value={salaryRange} />
                <GlanceCell
                  label="Business unit"
                  value={opportunityData.businessUnit || null}
                />
                <GlanceCell
                  label="Career level"
                  value={opportunityData.careerLevel || null}
                />
                <GlanceCell
                  label="Employment type"
                  value={opportunityData.employmentType || null}
                />
              </div>
            </RoCard>

            {/* Job Description */}
            {(opportunityData.description ||
              opportunityData.additionalRequirements) && (
              <RoCard icon={<FileText size={16} />} title="Job Description">
                {opportunityData.description && (
                  <MarkdownBody source={opportunityData.description} />
                )}
                {opportunityData.additionalRequirements && (
                  <div
                    className="border-t border-border"
                    style={{ marginTop: 14, paddingTop: 14 }}
                  >
                    <RoSub>Additional Requirements</RoSub>
                    <p
                      className="text-muted-foreground"
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontSize: 13,
                        lineHeight: 1.62,
                        margin: 0,
                      }}
                    >
                      {opportunityData.additionalRequirements}
                    </p>
                  </div>
                )}
              </RoCard>
            )}

            {/* Skills & Knowledge */}
            {opportunityData.skills?.length > 0 && (
              <RoCard icon={<Zap size={16} />} title="Skills & Knowledge">
                {reqSkills.length > 0 && (
                  <>
                    <RoSub>Required</RoSub>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {reqSkills.map((s) => (
                        <SkillChip
                          key={`req-${s.skill_id}`}
                          label={s.skill_id}
                          mandatory
                        />
                      ))}
                    </div>
                  </>
                )}
                {optSkills.length > 0 && (
                  <>
                    <RoSub topGap={reqSkills.length > 0 ? 14 : 0}>
                      Preferred
                    </RoSub>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {optSkills.map((s) => (
                        <SkillChip
                          key={`opt-${s.skill_id}`}
                          label={s.skill_id}
                          mandatory={false}
                        />
                      ))}
                    </div>
                  </>
                )}
              </RoCard>
            )}

            {/* Eligibility */}
            {(allEdu.length > 0 || allCerts.length > 0) && (
              <RoCard icon={<GraduationCap size={16} />} title="Eligibility">
                {allEdu.length > 0 && (
                  <>
                    <RoSub>Education</RoSub>
                    {allEdu.map((edu) => {
                      let fosDisplay = null;
                      if (edu.match_any_field) {
                        fosDisplay = 'Any specialization';
                      } else if (edu.field_of_study_id) {
                        fosDisplay =
                          fieldOfStudyMap[edu.field_of_study_id] ||
                          String(edu.field_of_study_id);
                      }
                      const label = [
                        getQualificationDisplayName(edu.qualification_type),
                        fosDisplay,
                      ]
                        .filter(Boolean)
                        .join(' — ');
                      return (
                        <div
                          key={`${edu.qualification_type || ''}-${edu.field_of_study_id ?? ''}`}
                          className="text-foreground-strong"
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 9,
                            fontSize: 13,
                            lineHeight: 1.5,
                            padding: '4px 0',
                          }}
                        >
                          <span
                            className="text-primary"
                            style={{
                              flexShrink: 0,
                              marginTop: 1,
                              display: 'flex',
                            }}
                          >
                            <Check size={15} />
                          </span>
                          <span>
                            {label}
                            {!edu.is_mandatory && (
                              <span
                                className="text-amber-700 dark:text-amber-500"
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  opacity: 0.7,
                                  marginLeft: 6,
                                }}
                              >
                                (optional)
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}
                {allCerts.length > 0 && (
                  <div style={{ marginTop: allEdu.length > 0 ? 16 : 0 }}>
                    <RoSub>Certifications</RoSub>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {allCerts.map((c) => (
                        <SkillChip
                          key={`cert-${c.certification_id}`}
                          label={c.certification_id}
                          mandatory={c.is_mandatory}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </RoCard>
            )}

            {/* Languages */}
            {allLangs.length > 0 && (
              <RoCard icon={<Globe size={16} />} title="Languages">
                {allLangs.map((l) => {
                  const langName =
                    languageMap[l.language_id] || `Language ${l.language_id}`;
                  const proficiency = l.min_proficiency
                    ? PROFICIENCY_LABEL[l.min_proficiency] || l.min_proficiency
                    : null;
                  return (
                    <div
                      key={`lang-${l.language_id}`}
                      className="text-foreground-strong"
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 9,
                        fontSize: 13,
                        lineHeight: 1.5,
                        padding: '4px 0',
                      }}
                    >
                      <span
                        className="text-primary"
                        style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}
                      >
                        <Check size={15} />
                      </span>
                      <span>
                        {langName}
                        {proficiency && (
                          <span
                            className="text-muted-foreground"
                            style={{ fontWeight: 400 }}
                          >
                            {' '}
                            — {proficiency}
                          </span>
                        )}
                        {!l.is_mandatory && (
                          <span
                            className="text-amber-700 dark:text-amber-500"
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              opacity: 0.7,
                              marginLeft: 6,
                            }}
                          >
                            (optional)
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </RoCard>
            )}

            {/* Locations & Openings (CSE) / Locations & Requested (TA) */}
            <RoCard
              icon={<MapPin size={16} />}
              title={isCse ? 'Locations & Openings' : 'Locations & Requested'}
            >
              {isRequirementLocationsLoading && (
                <RoEmpty>Loading locations…</RoEmpty>
              )}
              {!isRequirementLocationsLoading &&
                requirementLocations.length === 0 && (
                  <RoEmpty>No location data available.</RoEmpty>
                )}
              {!isRequirementLocationsLoading &&
                requirementLocations.map((loc, i) => (
                  <div
                    key={loc?.requirement_location_id || i}
                    className={cn(
                      'flex flex-col gap-1 py-[11px]',
                      i < requirementLocations.length - 1 &&
                        'border-b border-border'
                    )}
                  >
                    <div
                      className="text-foreground-strong"
                      style={{ fontSize: 13.5, fontWeight: 650 }}
                    >
                      {loc.location_name}
                    </div>
                    <div
                      className="text-muted-foreground"
                      style={{
                        fontSize: 12,
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      {isCse ? (
                        <span>
                          <b className="text-foreground-strong">
                            {loc.calculated_headcount ?? 0}
                          </b>{' '}
                          opening
                          {(loc.calculated_headcount ?? 0) !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span>
                          <b className="text-foreground-strong">
                            {loc.candidates_requested ?? 0}
                          </b>{' '}
                          requested
                        </span>
                      )}
                      {loc.employment_type && (
                        <>
                          <MetaDot />
                          <span>{loc.employment_type}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </RoCard>

            {/* Timeline */}
            <RoCard icon={<Calendar size={16} />} title="Timeline">
              <GlanceRow
                label="Posted on"
                value={fmtDate(opportunityData.postedOn)}
              />
              <GlanceRow
                label="Closing date"
                value={fmtDate(opportunityData.closingDate)}
                last={!opportunityData.archivedOn}
              />
              {opportunityData.archivedOn && (
                <GlanceRow
                  label="Archived on"
                  value={fmtDate(opportunityData.archivedOn)}
                  last
                />
              )}
            </RoCard>
          </div>
        </div>
      </div>

      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        opportunityId={id}
      />

      <Alert
        isOpen={claimConfirmOpen}
        onClose={() => setClaimConfirmOpen(false)}
        title="Claim Opportunity"
        description="Claiming this opportunity assigns it to you as the responsible CSE. Do you want to continue?"
        cancelText="Cancel"
        onCancel={() => setClaimConfirmOpen(false)}
        continueText={
          claimOpportunityMutation.isPending ? 'Claiming...' : 'Claim'
        }
        onContinue={handleClaim}
      />
    </>
  );
};

export default OpportunityDetails;
